import { useEffect, useState } from "react";
import { FormatNumber } from "utils/numberUtils";
import { IUnit } from "Dashboard/domain/IUnit";
import StockInput from "./StockInput";
import { useSelector } from "react-redux";
import {
  requestDeletingReservation,
  resetOutputValue,
  selectState,
  setOutputValue,
  setSelectedReservationId,
  setUpdateInputItemPopover
} from "../inputSlice";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { commonService } from "Dashboard/services/common.service";
import DeleteRowModal from "./DeleteRowModal";
import Popover from "components/Popover/Popover";
import { useAppDispatch } from "hooks/hooks";
import UpdateStockInputItemPopover from "./UpdateStockInputItemPopover";
import { usePartnerOptions } from "Dashboard/scenes/Partners/hooks/PartnerOptions";
import Loader from "components/Loader/Loader";
import { FormatDateShort } from "utils/dateUtils";
import { IStockReservation } from "Dashboard/scenes/Stock/domain/IStockReservation";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import DeleteReservationModal from "./DeleteReservationModal";
import { hasWriteAccessTo } from "utils/authUtil";
import { STOCK_MODULE_NAME } from "Dashboard/scenes/Stock/domain/StockModule";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";
import { UseFormGetValues } from "react-hook-form";
import { IGetStockInputsRequestParams } from "Dashboard/scenes/Stock/domain/IGetStockInputsRequestParams";
import InfoTooltip from "components/Popup/InfoTooltip";
import { useWindowedRows } from "hooks/useWindowedRows";

interface IProps {
  lastTableElementRef?: any;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void,
  doSortBy: (newSortColumn: string) => void,
  getValuesSearch: UseFormGetValues<IGetStockInputsRequestParams>;
}

// Keep in sync with `.g-grid.g-content .row` height ($grid-content-row-height).
const ROW_HEIGHT = 20;

const TopSection = ({ lastTableElementRef, handleScroll, doSortBy, getValuesSearch }: IProps) => {
  const dispatch = useAppDispatch();
  const { user } = useSelector(selectStateAuth);
  const {
    loading,
    reservations,
    selectedReservationId,
    inputs,
    initialTotalValue,
    remainingTotalValue,
    totalQuantity,
    totalQuantityRemaining,
    updateInputItemPopover
  } = useSelector(selectState);
  const [, setUnits] = useState<IUnit[]>([]);
  const partnerOptions = usePartnerOptions();
  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch(), ["stock"]);

  // Virtualize only the inputs list; the reservations block above it stays
  // fully rendered and is measured by the hook as the content's leading offset.
  const { contentRef, startIndex, endIndex, topSpacer, bottomSpacer } =
    useWindowedRows({
      count: inputs.length,
      rowHeight: ROW_HEIGHT,
      overscanPx: 1000,
    });

  useEffect(() => {
    async function getUnits() {
      try {
        const units = await commonService.getUnits({});

        setUnits(units);
      } catch (error) {
        console.log(error);
      }
    }

    getUnits();
  }, []);

  useEffect(() => {
    dispatch(resetOutputValue());
    if (selectedReservationId !== undefined) {
      for (let reservation of reservations) {
        if (reservation.reservationId === selectedReservationId) {
          for (let item of reservation.items) {
            dispatch(
              setOutputValue({
                key: item.input.inputId,
                repository: item.input.repository,
                value: item.quantityAmount,
              })
            );
          }
        }
      }
    }
  }, [selectedReservationId, reservations, dispatch]);

  const getDestinationOfReservation = (reservation: IStockReservation) => {
    if (reservation.partner) {
      return ` za saradnika ${reservation.partner.shortName}`;
    }
    if (reservation.project) {
      let overrideText = "";
      if (reservation.projectOverride) {
        overrideText = ` (na otpremnici ${reservation.projectOverride.name})`;
      }
      return ` za projekat ${reservation.project.name}${overrideText}`;
    }
    if (reservation.employee) {
      return ` za zaposlenog ${reservation.employee.name}`;
    }
    return "";
  }

  return (
    <div className="lager-top-section">
      <div className="g-grid g-header">
        <div className="row">
          <div className="col lowercase center"></div>
          <div className="col lowercase center separator-line">br.računa</div>
          <div className="col lowercase center separator-line">br.otpremnice</div>
          <div className="col lowercase center separator-line">šifra art.</div>
          <div className="col separator-line"></div>
          <div className="col lowercase center separator-line clickable"
            onClick={() => doSortBy("NAME")}>ime
          </div>
          <div className="col lowercase center separator-line quantity-for-output">
            količina izdajem
          </div>
          <div className="col lowercase center separator-line">dobavljač</div>
          <div className="col lowercase center separator-line">kol ostalo</div>
          <div className="col lowercase center separator-line">mera</div>
          <div className="col lowercase center separator-line">ukupno ostalo</div>
          <div className="col lowercase center separator-line">količina</div>
          <div className="col lowercase center separator-line">cena po jed. m.</div>
          <div className="col lowercase center separator-line">cena transporta</div>
          <div className="col lowercase center separator-line">ukupno uneto</div>
          <div className="col lowercase center separator-line clickable"
            onClick={() => doSortBy("DATE")}>datum ulaza
          </div>
          <div className="col separator-line"></div>
          <div className="col lowercase center separator-line">dovezao</div>
          <div className="col lowercase center separator-line">napomena</div>
        </div>
      </div>

      <div className="g-grid g-content" onScroll={handleScroll}>
        {isSearchFormEmpty && reservations &&
          reservations.map((reservation, index) => {
            return <>
              <div
                className={`reservation-container ${selectedReservationId === reservation.reservationId ? 'active' : ''}`}
                onClick={hasWriteAccessTo(user, STOCK_MODULE_NAME)
                  ? () => dispatch(setSelectedReservationId(reservation.reservationId))
                  : undefined}
              >
                <div className="reservation-summary">
                  {hasWriteAccessTo(user, STOCK_MODULE_NAME) &&
                    <ToolboxButton
                      icon={ToolboxButtonIcon.Delete}
                      tooltip="Obriši"
                      noCircle={true}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(requestDeletingReservation(reservation));
                      }}
                    />
                  }
                  <div className="reservation-checkbox-container">
                    <span className="xxx">
                      <input type="checkbox" checked={reservation.reservationId === selectedReservationId}>
                      </input>
                      <span className="special-ch" onClick={(e) => {
                        e.stopPropagation();
                        if (reservation.reservationId === selectedReservationId) {
                          dispatch(setSelectedReservationId(undefined));
                        } else {
                          dispatch(setSelectedReservationId(reservation.reservationId));
                        }
                      }}></span>
                    </span>
                  </div>
                  rezervacija korisnika {reservation.createdByUsername}{getDestinationOfReservation(reservation)} za dan {FormatDateShort(reservation.date)}
                </div>
                {reservation.items.map(item => <StockInput
                  lastTableElementRef={undefined}
                  key={`${item.input.inputId}`}
                  input={item.input}
                  reservationId={reservation.reservationId}
                  reservedQuantityAmount={item.quantityAmount}
                  partnerOptions={partnerOptions}
                  index={index} />)
                }
              </div>
            </>;
          })}
        {isSearchFormEmpty && reservations && reservations.length > 0 && <div className="reservation-container-end">stanje lagera</div>}

        <div ref={contentRef}>
          {topSpacer > 0 && <div style={{ height: topSpacer }} aria-hidden />}
          {inputs &&
            inputs.slice(startIndex, endIndex).map((input, offset) => {
              const index = startIndex + offset;
              return <StockInput
                key={`${input.inputId}`}
                input={input}
                partnerOptions={partnerOptions}
                index={index} />;
            })}
          {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} aria-hidden />}
          {/* Sentinel at the bottom of the loaded rows; when it scrolls into
              view the paging hook fetches the next backend batch. */}
          <div ref={lastTableElementRef} aria-hidden />
        </div>

        <Popover
          isOpen={updateInputItemPopover.isOpen}
          onClose={() => {
            dispatch(setUpdateInputItemPopover({ isOpen: false, itemRowIndex: -1 }));
          }}
          direction="bottom"
          title="Šifra, artikal, cena artikla"
          arrowStyle={{ top: "-20px" }}
          position={{
            top: updateInputItemPopover.top,
            left: updateInputItemPopover.left,
            offsetTop: 10,
          }}
        >
          <UpdateStockInputItemPopover />
        </Popover>
      </div>

      <div className="g-grid g-footer">
        {loading && <Loader />}

        <div className="row">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div className="col strong">
            <div className="bg-item bg-item-important right danger">
              <span className="text-small">{FormatNumber(totalQuantityRemaining)}</span>
            </div>
          </div>
          <div className="col strong total-price">
            <div className="bg-item bg-item-important overflowMinWidth right">
              <InfoTooltip classesProp="bg-text-overflow text-small"
                tooltip={`${FormatNumber(remainingTotalValue)} rsd`}>
                {FormatNumber(remainingTotalValue)} rsd
              </InfoTooltip>
            </div>
          </div>
          <div className="col strong">
            <div className="bg-item bg-item-important right">
              <span className="text-small">{FormatNumber(totalQuantity)}</span>
            </div>
          </div>
          <div></div>
          <div></div>
          <div className="col strong total-price">
            <div className="bg-item bg-item-important overflowMinWidth">
              <InfoTooltip classesProp="bg-text-overflow text-small"
                tooltip={`${FormatNumber(initialTotalValue)} rsd`}>
                {FormatNumber(initialTotalValue)} rsd
              </InfoTooltip>
            </div>
          </div>
          <div></div>
          <div></div>
        </div>
      </div>

      <DeleteRowModal />
      <DeleteReservationModal />
    </div>
  );
};

export default TopSection;
